import './polyfills'
import ReactDOM from 'react-dom'
import {
  ApolloProvider,
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  defaultDataIdFromObject,
  StoreObject,
  NormalizedCacheObject,
} from '@apollo/client'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { onError, ErrorResponse } from '@apollo/client/link/error'
import { AuthProvider } from './context/auth-context'
import { localStorageKey, logout } from './context/auth-client'
import { App } from './App'
import { AntDesignAndMomentLocaleProvider } from './AntDesignAndMomentLocaleProvider'
import * as serviceWorker from './serviceWorker'
import './styles/index.less'
import { getCookie, deleteCookie, jwtTokenCookieName } from './shared/cookieUtil'
import possibleTypes from './types'

const httpLink = createUploadLink({
  uri: '/graphql',
  credentials: 'include',
})

const stripTypenames = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(stripTypenames)
  } else if (value !== null && typeof value === 'object' && !(value instanceof File)) {
    const newObject = {} as any
    for (const property in value) {
      if (property !== '__typename') {
        newObject[property] = stripTypenames(value[property])
      }
    }
    return newObject
  } else {
    return value
  }
}

const typenameMiddleware = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = stripTypenames(operation.variables)
  }
  return forward(operation)
})

const authLink: ApolloLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem(localStorageKey)
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }))
  return forward(operation)
})

const logoutLink = onError((error: ErrorResponse) => {
  if (
    error.graphQLErrors &&
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0].extensions &&
    error.graphQLErrors[0].extensions.errorKey === 'accessViolation.unauthorized'
  ) {
    logout()
    window.location.href = '/'
  }
})

export const customApolloLink = (): ApolloLink => ApolloLink.from([typenameMiddleware, logoutLink, authLink, httpLink])
export const createApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    link: customApolloLink(), //authLink.concat(httpLink),
    cache: new InMemoryCache({
      dataIdFromObject(responseObject) {
        const defaultId: string | undefined = defaultDataIdFromObject(responseObject)

        if (responseObject.hasOwnProperty('globalId')) {
          const globalId = (responseObject as StoreObject & { globalId: string }).globalId
          return `${defaultId}:${globalId}`
        }

        return defaultId
      },
      possibleTypes: possibleTypes.possibleTypes,
    }),
    // Really pass through the errors from server
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  })
}

//-- SSO Logic that HAS to be executed BEFORE Apollo Client is constructed
if (window.location.search.startsWith('?type=success')) {
  // expected URL /login-sso?type=success
  const tokenViaSSO = getCookie(jwtTokenCookieName)
  if (tokenViaSSO !== undefined) {
    localStorage.setItem(localStorageKey, tokenViaSSO)
    deleteCookie(jwtTokenCookieName)
  }
}

export const client = createApolloClient()

ReactDOM.render(
  <ApolloProvider client={client}>
    <AuthProvider>
      <AntDesignAndMomentLocaleProvider>
        <App />
      </AntDesignAndMomentLocaleProvider>
    </AuthProvider>
  </ApolloProvider>,
  document.getElementById('root'),
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister()
